import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import apiFetch from '../utils/apiFetch'
import {
  ensureVisitorId,
  getLocalWishlist,
  saveLocalWishlist,
  makeWishQuery,
  LS_KEY,
  clearLocalWishlist,
} from '../utils/wishlistStorage'

const AccountContext = createContext(null)

export function AccountProvider({ children }) {
  // --- account state ---
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)

  // --- wishlist state  ---
  const [queries, setQueries] = useState([])
  const didInitialLoad = useRef(false)

  // ===== Helpers (wishlist) =====
  const normalizeKey = useCallback(q => {
    const f = (q.features || []).slice().sort().join('|')
    const lo = q.minPrice ?? ''
    const hi = q.maxPrice ?? ''
    return `${q.sku}|${lo}-${hi}|${f}`
  }, [])

  const upsert = useCallback((producer) => {
    setQueries(prev => {
      const next = producer(prev)
      if (didInitialLoad.current) {
        try {
          const visitorId = ensureVisitorId()
          saveLocalWishlist({ visitorId, queries: next })
        } catch (e) {
          console.warn('Wishlist save failed', e)
        }
      }
      return next
    })
  }, [])

  const addQuery = useCallback(({ product, features, minPrice, maxPrice }) => {
    const q = makeWishQuery({ product, features, minPrice, maxPrice })
    upsert(prev => {
      const key = normalizeKey(q)
      const idx = prev.findIndex(p => normalizeKey(p) === key)
      if (idx >= 0) {
        const now = Date.now()
        const copy = prev.slice()
        copy[idx] = { ...copy[idx], updatedAt: now }
        return copy
      }
      return [...prev, q]
    })
    return q
  }, [normalizeKey, upsert])

  const updateQuery = useCallback((id, updates) => {
    const now = Date.now()
    upsert(prev => prev.map(q => {
      if (q.id !== id) return q
      const patch = typeof updates === 'function' ? updates(q) : updates
      return { ...q, ...patch, updatedAt: now }
    }))
  }, [upsert])

  const removeQuery = useCallback(id => {
    upsert(prev => prev.filter(q => q.id !== id))
  }, [upsert])

  const setAll = useCallback(nextList => {
    setQueries(Array.isArray(nextList) ? nextList : [])
  }, [])

  const mergeFromServer = useCallback((serverList = []) => {
    const byKey = new Map()
    const put = q => byKey.set(normalizeKey(q), q)
    queries.forEach(put)
    const toLocal = (sv) => {
      const key = normalizeKey(sv)
      const existing = byKey.get(key)
      return {
        id: existing?.id || String(sv.id),
        serverId: sv.id ?? null,
        sku: sv.sku,
        objectId: sv.algoliaObjectId ?? null,
        features: Array.isArray(sv.features) ? sv.features : [],
        minPrice: sv.minPrice ?? null,
        maxPrice: sv.maxPrice ?? null,
        inStock: !!sv.inStock,
        onHold: !!sv.onHold,
        initialPrice: sv.initialPrice ?? null,
        createdAt: +new Date(sv.createdAt || Date.now()),
        updatedAt: +new Date(sv.updatedAt || Date.now()),
      }
    }
    serverList.forEach(sv => {
      const k = normalizeKey(sv)
      const incoming = toLocal(sv)
      const curr = byKey.get(k)
      if (!curr || (incoming.updatedAt || 0) > (curr.updatedAt || 0)) {
        byKey.set(k, incoming)
      }
    })
    setAll(Array.from(byKey.values()))
  }, [normalizeKey, queries, setAll])

  // ===== Initial local load (wishlist offline cache) =====
  useEffect(() => {
    const { queries: loaded } = getLocalWishlist()
    didInitialLoad.current = true
    setQueries(Array.isArray(loaded) ? loaded.filter(Boolean) : [])
  }, [])

  // ===== Cross-tab sync via localStorage =====
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onStorage = e => {
      if (e.key !== LS_KEY) return
      try {
        const data = JSON.parse(e.newValue || '{}')
        if (Array.isArray(data?.queries)) {
          setQueries(prev => {
            const byId = new Map(prev.map(q => [q.id, q]))
            for (const incoming of data.queries) {
              const existing = byId.get(incoming.id)
              if (!existing || (incoming.updatedAt || 0) > (existing.updatedAt || 0)) {
                byId.set(incoming.id, incoming)
              }
            }
            return Array.from(byId.values())
          })
        }
      } catch {/* ignore */}
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // ===== Account probe (1 request: session + preload wishlist) =====
  const probe = useCallback(async (force = false) => {
    try {
      if(isAuthenticated && !force) {
        return
      }
      const res = await apiFetch('/wishlist/my', { method: 'GET' })
      if (res.status === 200) {
        setIsAuthenticated(true)
        const { user, wishlist } = await res.json()
        setUser(user || null)
        mergeFromServer(wishlist)
      } else {
        setIsAuthenticated(false)
      }
    } catch {
      setIsAuthenticated(false)
    } finally {
      setLoading(false)
    }
  }, [mergeFromServer, isAuthenticated])

  useEffect(() => { probe() }, [probe])

  // ===== Auth actions =====
  const login = useCallback(async ({ email, password }) => {
    const res = await apiFetch('/account/login', { method: 'POST', body: { email, password } })
    if (!res.ok) {
      const j = await res.json().catch(() => ({}))
      throw new Error(j?.error || 'login_failed')
    }
    setIsAuthenticated(true)
    setUser(u => u || { email }) // placeholder
    await probe(true) 
    return true
  }, [probe])

  const logout = useCallback(async () => {
    try {
      await apiFetch('/account/logout', { method: 'POST' })
    } finally {
      setIsAuthenticated(false)
      setUser(null)
      setAll([])
      clearLocalWishlist()
    }
  }, [setAll])

  const requestMagicLink = useCallback(async ({ email, name, phone, consent }) => {
    const res = await apiFetch('/account/request-token', {
      method: 'POST',
      body: { email, ...(name ? { name } : {}), ...(phone ? { phone } : {}), ...(consent ? { consent: true } : {}) },
    })
    if (!res.ok) throw new Error('request_magic_link_failed')
    return res.json().catch(() => ({}))
  }, [])

  const requestSetPassword = useCallback(async ({ email, password }) => {
    const res = await apiFetch('/account/request-set-password', {
      method: 'POST',
      body: { email, password },
    })
    if (!res.ok) {
      const j = await res.json().catch(() => ({}))
      throw new Error(j?.error || 'request_set_password_failed')
    }
    return res.json()
  }, [])

  const requestResetPassword = useCallback(async ({ email }) => {
    const res = await apiFetch('/account/request-reset-password', {
      method: 'POST',
      body: { email },
    })
    if (!res.ok) throw new Error('request_reset_password_failed')
    return res.json().catch(() => ({}))
  }, [])

  const confirmResetPassword = useCallback(async ({ token, password }) => {
    const res = await apiFetch('/account/confirm-reset-password', {
      method: 'POST',
      body: { token, password },
    })
    if (!res.ok) {
      const j = await res.json().catch(() => ({}))
      throw new Error(j?.error || 'confirm_reset_password_failed')
    }
    return res.json()
  }, [])

  const changePassword = useCallback(async (_args) => {
    throw new Error('change_password_not_implemented')
  }, [])

  // ===== Wishlist actions (HTTP) =====
  const rateItem = useCallback((id, sku, liked) => {
    return apiFetch(`/wishlist/rate/${id}/${sku}`, {
      method: 'POST',
      body: { liked: !!liked },
    })
  }, [])

  const addAndPersist = useCallback(async args => {
    const q = addQuery(args) // local state first
    try {
      const res = await apiFetch('/wishlist', {
        method: 'POST',
        body: {
          sku: q.sku,
          features: q.features,
          minPrice: q.minPrice,
          maxPrice: q.maxPrice,
          initialPrice: q.initialPrice,
          inStock: q.inStock,
          onHold: q.onHold,
        },
      })
      if (res.ok) {
        const row = await res.json()
        updateQuery(q.id, { serverId: row.id, updatedAt: Date.now() })
      }
    } catch {
      /* offline / retry later, keep local cache  */
    }
    return q
  }, [addQuery, updateQuery])

  const patchAndPersist = useCallback(async (id, updater) => {
    const curr = (queries || []).find(q => q.id === id) || {}
    const patch = typeof updater === 'function' ? updater(curr) : updater
    updateQuery(id, () => patch)
    if (curr.serverId) {
      try {
        await apiFetch(`/wishlist/${curr.serverId}`, { method: 'PATCH', body: patch })
      } catch {/* offline */}
    }
  }, [queries, updateQuery])

  const removeAndPersist = useCallback(async id => {
    const curr = (queries || []).find(q => q.id === id) || {}
    removeQuery(id)
    if (curr?.serverId) {
      try {
        await apiFetch(`/wishlist/${curr.serverId}`, { method: 'DELETE' })
      } catch {/* offline */}
    }
  }, [queries, removeQuery])

  // Handle URL token (login via magic / set_password) -> GET /wishlist?token=...
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (!token) return
    apiFetch(`/wishlist/my?token=${encodeURIComponent(token)}`, { method: 'GET' })
      .then(async res => {
        if (res.status === 200) {
          const { user, wishlist } = await res.json()
          setUser(user || null)
          mergeFromServer(wishlist)
          setIsAuthenticated(true)
        }
      })
      .finally(() => {
        const url = new URL(window.location.href)
        url.searchParams.delete('token')
        window.history.replaceState({}, '', url)
      })
  }, [])

  const value = useMemo(() => ({
    loading,
    isAuthenticated,
    user,

    // account
    login,
    logout,
    requestMagicLink,
    requestSetPassword,
    requestResetPassword,
    confirmResetPassword,
    changePassword,
    recheck: probe,

    // wishlist (namespaced)
    wishlist: {
      queries,
      add: addAndPersist,
      update: patchAndPersist,
      remove: removeAndPersist,
      rate: rateItem,
      mergeFromServer,
      setAll,
      snapshot: () => ({ queries }),
    },
  }), [
    loading, isAuthenticated, user,
    login, logout, requestMagicLink, requestSetPassword, requestResetPassword, confirmResetPassword, changePassword, probe,
    queries, addAndPersist, patchAndPersist, removeAndPersist, rateItem, mergeFromServer, setAll
  ])

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
}

export default function useAccount() {
  return useContext(AccountContext)
}
